import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Users, ShoppingCart, Handshake, Building2, Plus, Pencil, Check, Calendar
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TODAY = new Date().toISOString().split("T")[0];
const MESES_NOME = ["Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const formatCurrency = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

export default function HubEmpresaFuncPage() {
  const { token } = useParams();
  const [func, setFunc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registros, setRegistros] = useState({ clientes: [], revendas: [], parceiros: [] });
  const [submitting, setSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState(null);

  const now = new Date();
  const mes = now.getMonth() + 1;
  const ano = now.getFullYear();

  // Forms state
  const [clienteForm, setClienteForm] = useState({ nome: "", valor: "", data: TODAY });
  const [revendaForm, setRevendaForm] = useState({ servidor: "", quantidade: "", valor_unitario: "", data: TODAY, descricao: "" });
  const [parceiroForm, setParceiroForm] = useState({ nome: "", valor: "", data: TODAY, descricao: "" });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/hub-empresa/funcionarios/publico/${token}`);
        setFunc(res.data);
        const reg = await axios.get(`${API}/api/hub-empresa/funcionarios/publico/${token}/meus-registros?mes=${mes}&ano=${ano}`);
        setRegistros(reg.data);
      } catch {
        setError("Link invalido ou funcionario desativado");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, mes, ano]);

  const fetchRegistros = async () => {
    try {
      const reg = await axios.get(`${API}/api/hub-empresa/funcionarios/publico/${token}/meus-registros?mes=${mes}&ano=${ano}`);
      setRegistros(reg.data);
    } catch {}
  };

  // Submit handlers
  const submitCliente = async (e) => {
    e.preventDefault();
    if (!clienteForm.nome || !clienteForm.valor) { toast.error("Preencha nome e valor"); return; }
    setSubmitting(true);
    try {
      if (editingItem && editingType === "clientes") {
        await axios.put(`${API}/api/hub-empresa/funcionarios/publico/${token}/clientes/${editingItem.id}`, clienteForm);
        toast.success("Cliente atualizado!");
      } else {
        await axios.post(`${API}/api/hub-empresa/funcionarios/publico/${token}/clientes`, { ...clienteForm, mes, ano });
        toast.success("Cliente adicionado!");
      }
      setClienteForm({ nome: "", valor: "", data: TODAY });
      setEditingItem(null); setEditingType(null);
      fetchRegistros();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const submitRevenda = async (e) => {
    e.preventDefault();
    if (!revendaForm.servidor) { toast.error("Preencha o servidor"); return; }
    setSubmitting(true);
    try {
      if (editingItem && editingType === "revendas") {
        await axios.put(`${API}/api/hub-empresa/funcionarios/publico/${token}/revendas/${editingItem.id}`, revendaForm);
        toast.success("Revenda atualizada!");
      } else {
        await axios.post(`${API}/api/hub-empresa/funcionarios/publico/${token}/revendas`, { ...revendaForm, mes, ano });
        toast.success("Revenda adicionada!");
      }
      setRevendaForm({ servidor: "", quantidade: "", valor_unitario: "", data: TODAY, descricao: "" });
      setEditingItem(null); setEditingType(null);
      fetchRegistros();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const submitParceiro = async (e) => {
    e.preventDefault();
    if (!parceiroForm.nome || !parceiroForm.valor) { toast.error("Preencha nome e valor"); return; }
    setSubmitting(true);
    try {
      if (editingItem && editingType === "parceiros") {
        await axios.put(`${API}/api/hub-empresa/funcionarios/publico/${token}/parceiros/${editingItem.id}`, parceiroForm);
        toast.success("Parceiro atualizado!");
      } else {
        await axios.post(`${API}/api/hub-empresa/funcionarios/publico/${token}/parceiros`, { ...parceiroForm, mes, ano });
        toast.success("Parceiro adicionado!");
      }
      setParceiroForm({ nome: "", valor: "", data: TODAY, descricao: "" });
      setEditingItem(null); setEditingType(null);
      fetchRegistros();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const startEdit = (item, type) => {
    setEditingItem(item); setEditingType(type);
    if (type === "clientes") setClienteForm({ nome: item.nome, valor: item.valor, data: item.data });
    if (type === "revendas") setRevendaForm({ servidor: item.servidor, quantidade: item.quantidade, valor_unitario: item.valor_unitario, data: item.data, descricao: item.descricao || "" });
    if (type === "parceiros") setParceiroForm({ nome: item.nome, valor: item.valor, data: item.data, descricao: item.descricao || "" });
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return <div className="min-h-screen bg-background flex items-center justify-center"><Card className="max-w-md w-full mx-4"><CardContent className="p-8 text-center"><Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" /><p className="text-muted-foreground">{error}</p></CardContent></Card></div>;

  return (
    <div className="min-h-screen bg-background p-4 sm:p-8">
      <Toaster position="top-right" richColors />
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-blue-500/10"><Building2 className="w-8 h-8 text-blue-400" /></div>
                <div>
                  <h1 className="text-xl font-bold">Hub Empresa</h1>
                  <p className="text-sm text-muted-foreground">Funcionario: <span className="font-semibold text-primary">{func.nome}</span></p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border/50">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">{MESES_NOME[mes - 1]} / {ano}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="clientes" className="w-full">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="clientes" className="gap-1.5"><Users className="w-4 h-4" /> Clientes</TabsTrigger>
            <TabsTrigger value="revendas" className="gap-1.5"><ShoppingCart className="w-4 h-4" /> Revendas</TabsTrigger>
            <TabsTrigger value="parceiros" className="gap-1.5"><Handshake className="w-4 h-4" /> Parceiros</TabsTrigger>
          </TabsList>

          {/* Clientes Pagos */}
          <TabsContent value="clientes" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">{editingItem && editingType === "clientes" ? "Editar" : "Novo"} Cliente Pago</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={submitCliente} className="space-y-3">
                  <div className="space-y-1"><Label>Nome *</Label><Input value={clienteForm.nome} onChange={(e) => setClienteForm({...clienteForm, nome: e.target.value})} placeholder="Nome do cliente" /></div>
                  <div className="space-y-1"><Label>Valor (R$) *</Label><Input type="number" step="0.01" value={clienteForm.valor} onChange={(e) => setClienteForm({...clienteForm, valor: e.target.value})} placeholder="0.00" /></div>
                  <div className="space-y-1"><Label>Data</Label><Input type="date" value={clienteForm.data} onChange={(e) => setClienteForm({...clienteForm, data: e.target.value})} /></div>
                  <Button type="submit" disabled={submitting} className="w-full">{submitting ? "Salvando..." : editingItem && editingType === "clientes" ? "Atualizar" : "Adicionar"}</Button>
                  {editingItem && editingType === "clientes" && <Button type="button" variant="outline" className="w-full" onClick={() => { setEditingItem(null); setEditingType(null); setClienteForm({ nome: "", valor: "", data: TODAY }); }}>Cancelar Edicao</Button>}
                </form>
              </CardContent>
            </Card>
            {registros.clientes.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Meus registros ({registros.clientes.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {registros.clientes.map((c) => (
                    <div key={c.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                      <div><p className="text-sm font-medium">{c.nome}</p><p className="text-xs text-muted-foreground">{c.data} - {formatCurrency(c.valor)}</p></div>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(c, "clientes")}><Pencil className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Revendas */}
          <TabsContent value="revendas" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">{editingItem && editingType === "revendas" ? "Editar" : "Nova"} Revenda</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={submitRevenda} className="space-y-3">
                  <div className="space-y-1"><Label>Servidor *</Label><Input value={revendaForm.servidor} onChange={(e) => setRevendaForm({...revendaForm, servidor: e.target.value})} placeholder="Nome do servidor" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label>Quantidade</Label><Input type="number" step="0.01" value={revendaForm.quantidade} onChange={(e) => setRevendaForm({...revendaForm, quantidade: e.target.value})} /></div>
                    <div className="space-y-1"><Label>Valor Unitario</Label><Input type="number" step="0.01" value={revendaForm.valor_unitario} onChange={(e) => setRevendaForm({...revendaForm, valor_unitario: e.target.value})} /></div>
                  </div>
                  {revendaForm.quantidade && revendaForm.valor_unitario && (
                    <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-center text-sm font-semibold">
                      Total: {formatCurrency(parseFloat(revendaForm.quantidade || 0) * parseFloat(revendaForm.valor_unitario || 0))}
                    </div>
                  )}
                  <div className="space-y-1"><Label>Data</Label><Input type="date" value={revendaForm.data} onChange={(e) => setRevendaForm({...revendaForm, data: e.target.value})} /></div>
                  <div className="space-y-1"><Label>Descricao</Label><Textarea value={revendaForm.descricao} onChange={(e) => setRevendaForm({...revendaForm, descricao: e.target.value})} placeholder="Descricao (opcional)" /></div>
                  <Button type="submit" disabled={submitting} className="w-full">{submitting ? "Salvando..." : editingItem && editingType === "revendas" ? "Atualizar" : "Adicionar"}</Button>
                  {editingItem && editingType === "revendas" && <Button type="button" variant="outline" className="w-full" onClick={() => { setEditingItem(null); setEditingType(null); setRevendaForm({ servidor: "", quantidade: "", valor_unitario: "", data: TODAY, descricao: "" }); }}>Cancelar Edicao</Button>}
                </form>
              </CardContent>
            </Card>
            {registros.revendas.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Meus registros ({registros.revendas.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {registros.revendas.map((r) => (
                    <div key={r.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                      <div><p className="text-sm font-medium">{r.servidor}</p><p className="text-xs text-muted-foreground">{r.data} - {formatCurrency(r.valor_total)}</p></div>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(r, "revendas")}><Pencil className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Parceiros */}
          <TabsContent value="parceiros" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">{editingItem && editingType === "parceiros" ? "Editar" : "Novo"} Parceiro</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={submitParceiro} className="space-y-3">
                  <div className="space-y-1"><Label>Nome *</Label><Input value={parceiroForm.nome} onChange={(e) => setParceiroForm({...parceiroForm, nome: e.target.value})} placeholder="Nome do parceiro" /></div>
                  <div className="space-y-1"><Label>Valor (R$) *</Label><Input type="number" step="0.01" value={parceiroForm.valor} onChange={(e) => setParceiroForm({...parceiroForm, valor: e.target.value})} placeholder="0.00" /></div>
                  <div className="space-y-1"><Label>Data</Label><Input type="date" value={parceiroForm.data} onChange={(e) => setParceiroForm({...parceiroForm, data: e.target.value})} /></div>
                  <div className="space-y-1"><Label>Descricao</Label><Textarea value={parceiroForm.descricao} onChange={(e) => setParceiroForm({...parceiroForm, descricao: e.target.value})} placeholder="Descricao (opcional)" /></div>
                  <Button type="submit" disabled={submitting} className="w-full">{submitting ? "Salvando..." : editingItem && editingType === "parceiros" ? "Atualizar" : "Adicionar"}</Button>
                  {editingItem && editingType === "parceiros" && <Button type="button" variant="outline" className="w-full" onClick={() => { setEditingItem(null); setEditingType(null); setParceiroForm({ nome: "", valor: "", data: TODAY, descricao: "" }); }}>Cancelar Edicao</Button>}
                </form>
              </CardContent>
            </Card>
            {registros.parceiros.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Meus registros ({registros.parceiros.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {registros.parceiros.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                      <div><p className="text-sm font-medium">{p.nome}</p><p className="text-xs text-muted-foreground">{p.data} - {formatCurrency(p.valor)}</p></div>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(p, "parceiros")}><Pencil className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
